/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from 'react';
import { useReportStore } from '@store';
import {
  useRecordPreviewQuery,
  useRecordIntrospectionQuery,
} from '@common/cache';
import { IColumnDefinition } from '@common/types';
import { IRecordsPreviewFilters, IPreviewTableFilters } from '../../types.ts';
import { usePreviewContext } from '../contexts';

type PaginationFilters = Pick<IRecordsPreviewFilters, 'page' | 'limit'>;

const basePaginationFilters: PaginationFilters = {
  page: 1,
  limit: 100,
};

export const useRecordsTable = (group_name?: string) => {
  const { report, setIntrospection } = useReportStore();
  const { data } = useRecordIntrospectionQuery();
  const contextValues = usePreviewContext();
  const { filters, handleFilterChange } = contextValues;

  const [paginationFilters, setPaginationFilters] = useState<PaginationFilters>(
    basePaginationFilters,
  );

  useEffect(() => {
    if (data) {
      setIntrospection(data);
    }
  }, [data]);

  const onFilterChange = (newFilters: Partial<IPreviewTableFilters>) => {
    if (
      newFilters.limit !== paginationFilters.limit ||
      newFilters.page !== paginationFilters.page
    ) {
      setPaginationFilters({ page: newFilters.page, limit: newFilters.limit });
    } else {
      handleFilterChange(newFilters);
      setPaginationFilters({ ...paginationFilters, page: 1 });
    }
  };

  // const location = useLocation();
  // const isPreview = location.pathname.includes('preview');

  const groupByFilter = (() => {
    if (!filters.group_by) {
      return [];
    }

    if (group_name === null) {
      return [
        {
          field: filters.group_by,
          operator: '$isnull',
          value: true,
        },
      ];
    }

    return [
      {
        field: filters.group_by,
        operator: '$eq',
        value: group_name,
      },
    ];
  })();

  const { data: previewData, isFetching: isPreviewFetching } =
    useRecordPreviewQuery({
      ...(filters as any),
      ...paginationFilters,
      // enabled: isPreview,
      options: {
        ...filters.options,
        $and: [...(filters.options?.$and ?? []), ...groupByFilter],
      },
    });

  const entries = previewData?.items ?? [];
  const totalCount = previewData?.count ?? 0;

  const allPossibleColumns = data?.groups?.flatMap(o => o.fields) ?? [];

  const columns: IColumnDefinition[] =
    report?.options?.select
      .map(rep => {
        const column = allPossibleColumns.find(col => col.field === rep);
        return column;
      })
      .filter(o => o !== undefined)
      .map(o => ({
        id: o.field,
        label: o.label,
        sortable: true,
      })) ?? [];

  return {
    entries,
    columns,
    totalCount,
    isLoading: isPreviewFetching,
    contextValues,
    onFilterChange,
    filters: { ...filters, ...paginationFilters },
  };
};
